import React, { useState, useEffect } from 'react';
import { ugandaDistricts } from '../data/ugandaDistricts';
import { ugandaSubcounties } from '../data/ugandaSubcounties';
import { worldCountries } from '../data/worldCountries';
import { legalCharter } from '../config/legalCharter';
import LegalCharterModal from './LegalCharterModal';
import { startAuthentication } from '@simplewebauthn/browser';

export default function AuthModal({
    onAuthSuccessBroadcast,
    onCloseFormDrawer
}) {
    const baseUrl = 'https://ebakasi.onrender.com';
    const [authActionTab, setAuthActionTab] = useState('login');
    const [isAwaitingOtpVerification, setIsAwaitingOtpVerification] = useState(false);
    const [submittingPayload, setSubmittingPayload] = useState(false);
    const [clientSubmittedPin, setClientSubmittedPin] = useState('');
    const [verificationChannel, setVerificationChannel] = useState('email');
    const [districtSearchQuery, setDistrictSearchQuery] = useState("");
    const [isDistrictDropdownOpen, setIsDistrictDropdownOpen] = useState(false);

    // Wizard state for signup
    const [currentStep, setCurrentStep] = useState(1);
    const [stepErrors, setStepErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isRoleSelected, setIsRoleSelected] = useState(false);
    const [roleTransitioning, setRoleTransitioning] = useState(false);
    const [isMobileViewport, setIsMobileViewport] = useState(false);
    const [isWakingUp, setIsWakingUp] = useState(false);
    const [authError, setAuthError] = useState(null);

    // Track mobile viewport for responsive styling
    useEffect(() => {
        const checkMobile = () => setIsMobileViewport(window.innerWidth < 640);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

  // 📐 VIEWPORT TRANSITION ARCHITECTURE: Smoothly reset browser scroll axis on OTP action triggers
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const modalContainer = document.querySelector("[style*='maxHeight']") || document.querySelector("[style*='overflowY']");
    if (modalContainer) {
      modalContainer.scrollTop = 0;
    }
  }, [authActionTab, isAwaitingOtpVerification]);

  // Reset wizard step when auth tab changes
  useEffect(() => {
    setCurrentStep(1);
    setStepErrors({});
    setTouched({});
  }, [authActionTab, onCloseFormDrawer]);

  // 📐 REACTIVE VIEWPORT FOCUS ARCHITECTURE: Smoothly reset axis to top on view changes
  useEffect(() => {
    const targetContainer = document.querySelector("#auth-modal-scroll-container") || window;
    if (typeof targetContainer.scrollTo === "function") {
      targetContainer.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [authActionTab, isAwaitingOtpVerification]);
    const [charterAccepted, setCharterAccepted] = useState(false);
    const [showLegalCharter, setShowLegalCharter] = useState(false);

    // Resilient fetch with warm-up retry for cold starts
    const apiFetchWithRetry = async (url, options = {}, maxRetries = 3) => {
        let lastError = null;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const controller = new AbortController();
                const timeoutMs = attempt === 1 ? 90000 : 25000; // 90s on first attempt for cold start
                const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

                const res = await fetch(url, {
                    ...options,
                    signal: controller.signal,
                });

                clearTimeout(timeoutId);

                // Check for retryable server errors
                if (res.status >= 502 && res.status <= 504 && attempt < maxRetries) {
                    lastError = new Error(`Server error ${res.status}`);
                    await new Promise(resolve => setTimeout(resolve, 2000 * attempt)); // 2s, 4s backoff
                    continue;
                }

                return res;

            } catch (error) {
                lastError = error;

                // On first failure, try warm-up ping before retry
                if (attempt === 1) {
                    try {
                        setIsWakingUp(true);
                        const warmUpController = new AbortController();
                        const warmUpTimeout = setTimeout(() => warmUpController.abort(), 30000);
                        await fetch(`${baseUrl}/api/health-check`, {
                            signal: warmUpController.signal,
                        });
                        clearTimeout(warmUpTimeout);
                    } catch (warmUpErr) {
                        // Warm-up failure is non-critical, continue with retry
                        console.warn('Warm-up ping failed:', warmUpErr.message);
                    }
                }

                // Exponential backoff before retry
                if (attempt < maxRetries) {
                    await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
                }
            }
        }

        throw lastError || new Error('Service temporarily unavailable');
    };

    // Load Google Identity Services script
    useEffect(() => {
        const loadGoogleScript = () => {
            if (window.google && process.env.REACT_APP_GOOGLE_CLIENT_ID) {
                window.google.accounts.id.initialize({
                    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                    callback: handleGoogleCredentialResponse,
                });
                window.google.accounts.id.renderButton(
                    document.getElementById('google-signin-button'),
                    { theme: 'outline', size: 'large', text: 'signin_with', width: '100%' }
                );
            }
        };

        // Load script if not already loaded
        if (!window.google) {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = loadGoogleScript;
            document.head.appendChild(script);
        } else {
            loadGoogleScript();
        }
    }, []);

    const handleGoogleCredentialResponse = async (response) => {
        try {
            setSubmittingPayload(true);
            setAuthError(null);

            const res = await apiFetchWithRetry(`${baseUrl}/api/auth/oauth/google`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken: response.credential }),
            });
            const data = await res.json();
            if (res.ok) {
                localStorage.setItem('ebakasiJwtToken', data.token);
                localStorage.setItem('ebakasiUser', JSON.stringify(data.user));
                if (onAuthSuccessBroadcast) {
                    onAuthSuccessBroadcast(data.user);
                }
                onCloseFormDrawer();
            } else {
                setAuthError(data.error || 'Google sign-in failed');
            }
        } catch (err) {
            setAuthError(err.message || 'Google sign-in failed');
        } finally {
            setSubmittingPayload(false);
            setIsWakingUp(false);
        }
    }; 

    // ⚙️ MODERN EBAKASI INDUSTRIAL COMPREHENSIVE DATA DICTIONARY STATE MATRIX
    const [credentialsForm, setCredentialsForm] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'buyer', // buyer, vendor, machinery_hire, driver

        // 🛒 Buyer Specific Fields
        phoneNumber: '',
        phoneCountryDialCode: '256', // Default to Uganda

        // 🏭 Verified Vendor / Merchant / Depot Fields
        enterprisePlantName: '',
        nationalIdNinNumber: '',
        payoutMethod: 'bank', // bank or momo
        merchantSettlementPayoutCode: '',

        // Custom registration fields (SUPER_ADMIN-configurable)
        customFields: {},
        mobileMoneyLineNumber: '',
        bankTargetName: 'Stanbic Bank Uganda',
        bankAccountNumberId: '',
        vendorOfferedCommissionPercent: '3.0',

        // 🚜 Heavy Machinery For Hire Provider Specific Fields
        machineryFleetSize: '1-5 Units',
        primaryMachineryType: 'Excavators',
        permitRegistrationNumber: '',
        suggestedCommission: '10', // Commission percentage suggested by machinery hire

        // 🚛 Driver/Fleet Operator Specific Fields
        truckRegistrationPlates: '',
        insurancePolicyNumber: '',

        // 🪪 Transit Cargo Driver Fields
        drivingLicenseNumber: '',
        yearsOfHeavyTransitExperience: 'Under 2 Years',
        preferredTransitRouteRegion: 'Central Region (Kampala/Wakiso)',

        // 📍 Consolidated Location Block
        location: {
            country: 'Uganda',
            district: '',
            subcounty: '',
            villageStreet: '',
            specificAddress: '',
            gps: null,
        },
    });

    // Dynamic step count based on role (must be after credentialsForm is declared)
    const SPECIALIZED_ROLES = ['vendor', 'machinery_hire', 'driver'];
    const totalSteps = SPECIALIZED_ROLES.includes(credentialsForm.role) ? 5 : 4;

    // Force vendor payoutMethod to 'bank' (Payment Intelligence restriction)
    useEffect(() => {
        if (credentialsForm.role === 'vendor' && credentialsForm.payoutMethod !== 'bank') {
            setCredentialsForm(prev => ({...prev, payoutMethod: 'bank'}));
        }
    }, [credentialsForm.role]);

    const [recoveryEmail, setRecoveryEmail] = useState('');
    
    // Village autocomplete state
    const [villageSuggestions, setVillageSuggestions] = useState([]);
    const [showVillageSuggestions, setShowVillageSuggestions] = useState(false);
    const [isCustomVillage, setIsCustomVillage] = useState(false);
    const [villageSearchTimeout, setVillageSearchTimeout] = useState(null);

    // Debounced village search function
    const searchVillages = async (subcounty, searchTerm) => {
        if (!subcounty || !searchTerm || searchTerm.length < 2) {
            setVillageSuggestions([]);
            setShowVillageSuggestions(false);
            return;
        }

        try {
            const response = await fetch(`${baseUrl}/api/locations/villages?subcounty=${encodeURIComponent(subcounty)}&search=${encodeURIComponent(searchTerm)}&limit=10`);
            const data = await response.json();

            if (data.villages && data.villages.length > 0) {
                setVillageSuggestions(data.villages.map(v => v.name));
                setShowVillageSuggestions(true);
                setIsCustomVillage(false);
            } else {
                setVillageSuggestions([]);
                setShowVillageSuggestions(false);
                setIsCustomVillage(true);
            }
        } catch (error) {
            console.error('Village search error:', error);
            setVillageSuggestions([]);
            setShowVillageSuggestions(false);
        }
    };

    const handleVillageInputChange = (e) => {
        const value = e.target.value;
        setCredentialsForm({...credentialsForm, location: {...credentialsForm.location, villageStreet: value}});
        
        // Clear previous timeout
        if (villageSearchTimeout) {
            clearTimeout(villageSearchTimeout);
        }

        // Only search if subcounty is selected and value has at least 2 characters
        if (credentialsForm.location.subcounty && value.length >= 2) {
            const timeout = setTimeout(() => {
                searchVillages(credentialsForm.location.subcounty, value);
            }, 300);
            setVillageSearchTimeout(timeout);
        } else {
            setVillageSuggestions([]);
            setShowVillageSuggestions(false);
        }
    };

    const handleVillageSelect = (villageName) => {
        setCredentialsForm({...credentialsForm, location: {...credentialsForm.location, villageStreet: villageName}});
        setVillageSuggestions([]);
        setShowVillageSuggestions(false);
        setIsCustomVillage(false);
    };

    const handleCustomVillageClick = () => {
        setIsCustomVillage(true);
        setShowVillageSuggestions(false);
    };
    const [recoveryPin, setRecoveryPin] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [isAwaitingRecoveryPin, setIsAwaitingRecoveryPin] = useState(false);
    const [gpsMessage, setGpsMessage] = useState('');

    // Custom registration fields (SUPER_ADMIN-configurable)
    const [registrationFormSchema, setRegistrationFormSchema] = useState([]);

    // Fetch registration form schema on mount
    useEffect(() => {
        const fetchRegSchema = async () => {
            try {
                const res = await fetch(`${baseUrl}/api/config/registration-form-schema`);
                if (res.ok) {
                    const data = await res.json();
                    setRegistrationFormSchema(data.registrationFormSchema || []);
                }
            } catch (err) {
                console.error('Failed to fetch registration form schema:', err);
            }
        };
        fetchRegSchema();
    }, [baseUrl]);

    // GPS capture handler
    const handleGpsCapture = () => {
        if (!navigator.geolocation) {
            setGpsMessage('❌ Geolocation not supported by this browser');
            return;
        }
        setGpsMessage('📍 Acquiring coordinates...');
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setCredentialsForm({
                    ...credentialsForm,
                    location: {
                        ...credentialsForm.location,
                        gps: {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        },
                    },
                });
                setGpsMessage('✓ Coordinates locked');
                setTimeout(() => setGpsMessage(''), 3000);
            },
            (error) => {
                setGpsMessage('❌ GPS access denied or failed');
                setTimeout(() => setGpsMessage(''), 3000);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    // Per-step validation function
    const validateStep = (step) => {
        const errors = {};

        if (step === 1) {
            // Profile Selection - role is set on click, no validation needed
        }

        if (step === 2) {
            // Access Credentials validation
            const nameRegex = /^[a-zA-Z\s\-'\u00C0-\u017F]+$/;
            if (!credentialsForm.name || credentialsForm.name.trim().length < 2 || !nameRegex.test(credentialsForm.name.trim())) {
                errors.name = 'Please enter a valid name (minimum 2 characters, letters only)';
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
            if (!emailRegex.test(credentialsForm.email.toLowerCase().trim())) {
                errors.email = 'Please enter a valid email address';
            }

            if (credentialsForm.password.length < 8) {
                errors.password = 'Password must be at least 8 characters long';
            }

            if (credentialsForm.password !== credentialsForm.confirmPassword) {
                errors.confirmPassword = 'Passwords do not match';
            }

            if (!credentialsForm.phoneNumber || credentialsForm.phoneNumber.length < 6 || credentialsForm.phoneNumber.length > 14) {
                errors.phoneNumber = 'Please enter a valid phone number (6-14 digits)';
            }

            // Validate custom registration fields
            registrationFormSchema
                .filter(field => !field.targetRole || field.targetRole === credentialsForm.role)
                .forEach((field) => {
                if (field.required) {
                    const value = credentialsForm.customFields?.[field.fieldId];
                    if (field.fieldType === 'checkbox') {
                        // Checkbox required means it must be checked
                        if (!value) {
                            errors[`custom_${field.fieldId}`] = `${field.fieldName} is required`;
                        }
                    } else {
                        // Other field types require non-empty value
                        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
                            errors[`custom_${field.fieldId}`] = `${field.fieldName} is required`;
                        }
                    }
                }
                // Additional validation based on field type and validation rules
                if (field.validation && credentialsForm.customFields?.[field.fieldId]) {
                    const value = credentialsForm.customFields[field.fieldId];
                    if (field.validation.min !== undefined && Number(value) < field.validation.min) {
                        errors[`custom_${field.fieldId}`] = `${field.fieldName} must be at least ${field.validation.min}`;
                    }
                    if (field.validation.max !== undefined && Number(value) > field.validation.max) {
                        errors[`custom_${field.fieldId}`] = `${field.fieldName} must be at most ${field.validation.max}`;
                    }
                    if (field.validation.pattern && !new RegExp(field.validation.pattern).test(value)) {
                        errors[`custom_${field.fieldId}`] = `${field.fieldName} format is invalid`;
                    }
                }
            });
        }

        if (step === 3) {
            const { location } = credentialsForm;
            const role = credentialsForm.role;

            // Location validation - all fields required for all roles
            if (!location.district || location.district.trim().length === 0) {
                errors.district = 'District is required';
            }
            if (!location.subcounty || location.subcounty.trim().length === 0) {
                errors.subcounty = 'Subcounty is required';
            }
            if (!location.villageStreet || location.villageStreet.trim().length === 0) {
                errors.villageStreet = 'Village / Street is required';
            }
            if (!location.specificAddress || location.specificAddress.trim().length === 0) {
                errors.specificAddress = 'Specific Address is required';
            }
            if (location.subcounty && location.subcounty.trim().length > 0) {
                const subcountyTrimmed = location.subcounty.trim();
                if (subcountyTrimmed.length < 2 || !/[aeiou]/i.test(subcountyTrimmed) || /[bcdfghjklmnpqrstvwxyz]{3,}/i.test(subcountyTrimmed)) {
                    errors.subcounty = 'Please enter a valid Subcounty';
                }
            }
            if (location.villageStreet && location.villageStreet.trim().length > 0) {
                const villageTrimmed = location.villageStreet.trim();
                if (villageTrimmed.length < 2 || !/[aeiou]/i.test(villageTrimmed) || /[bcdfghjklmnpqrstvwxyz]{3,}/i.test(villageTrimmed)) {
                    errors.villageStreet = 'Please enter a valid Village / Street';
                }
            }
            if (location.specificAddress && location.specificAddress.trim().length > 0) {
                const addressTrimmed = location.specificAddress.trim();
                if (addressTrimmed.length < 5 || !/[aeiou]/i.test(addressTrimmed) || /[bcdfghjklmnpqrstvwxyz]{3,}/i.test(addressTrimmed)) {
                    errors.specificAddress = 'Please enter a valid Specific Address (minimum 5 characters)';
                }
            }

            // NIN validation for buyer (optional but if present must be valid)
            if (role === 'buyer') {
                const rawNin = String(credentialsForm.nationalIdNinNumber || '').trim().toUpperCase();
                const userCountry = location.country || 'Uganda';
                if (rawNin && rawNin.length > 0) {
                    if (userCountry === 'Uganda') {
                        const ugandaNinRegex = /^(CM|CF)[A-Z0-9]{12}$/;
                        if (!ugandaNinRegex.test(rawNin)) {
                            errors.nationalIdNinNumber = 'Invalid NIN format for Uganda. Must be exactly 14 characters beginning with CM or CF';
                        }
                    } else {
                        if (!/^[A-Z0-9]{4,30}$/.test(rawNin)) {
                            errors.nationalIdNinNumber = 'Invalid National ID format. Must be 4-30 alphanumeric characters';
                        }
                    }
                }
            }
        }

        if (step === 4) {
            const role = credentialsForm.role;
            const userCountry = credentialsForm.location.country || 'Uganda';
            const rawNin = String(credentialsForm.nationalIdNinNumber || '').trim().toUpperCase();

            // NIN validation (required for specialized roles only - buyer NIN is on step 3)
            if (SPECIALIZED_ROLES.includes(role)) {
                if (!rawNin || rawNin.length === 0) {
                    errors.nationalIdNinNumber = 'National ID / NIN number is required';
                } else if (userCountry === 'Uganda') {
                    const ugandaNinRegex = /^(CM|CF)[A-Z0-9]{12}$/;
                    if (!ugandaNinRegex.test(rawNin)) {
                        errors.nationalIdNinNumber = 'Invalid NIN format for Uganda. Must be exactly 14 characters beginning with CM or CF';
                    }
                } else {
                    if (!/^[A-Z0-9]{4,30}$/.test(rawNin)) {
                        errors.nationalIdNinNumber = 'Invalid National ID format. Must be 4-30 alphanumeric characters';
                    }
                }
            }

            // Role-specific field validation
            if (role === 'vendor') {
                if (!credentialsForm.enterprisePlantName || credentialsForm.enterprisePlantName.trim().length === 0) {
                    errors.enterprisePlantName = 'Merchant name is required';
                }
                if (credentialsForm.payoutMethod === 'bank') {
                    if (!credentialsForm.bankAccountNumberId || credentialsForm.bankAccountNumberId.trim().length === 0) {
                        errors.bankAccountNumberId = 'Bank account number is required';
                    }
                }
                if (credentialsForm.payoutMethod === 'momo') {
                    if (!credentialsForm.merchantSettlementPayoutCode || credentialsForm.merchantSettlementPayoutCode.trim().length === 0) {
                        errors.merchantSettlementPayoutCode = 'Merchant code is required';
                    }
                    if (!credentialsForm.mobileMoneyLineNumber || credentialsForm.mobileMoneyLineNumber.trim().length === 0) {
                        errors.mobileMoneyLineNumber = 'Payout phone number is required';
                    }
                }
            }

            if (role === 'machinery_hire') {
                if (!credentialsForm.permitRegistrationNumber || credentialsForm.permitRegistrationNumber.trim().length === 0) {
                    errors.permitRegistrationNumber = 'Permit registration number is required';
                }
            }

            if (role === 'driver') {
                if (!credentialsForm.drivingLicenseNumber || credentialsForm.drivingLicenseNumber.trim().length === 0) {
                    errors.drivingLicenseNumber = 'Driving license number is required';
                }
                if (!credentialsForm.truckRegistrationPlates || credentialsForm.truckRegistrationPlates.trim().length === 0) {
                    errors.truckRegistrationPlates = 'Truck registration plates are required';
                }
                if (!credentialsForm.insurancePolicyNumber || credentialsForm.insurancePolicyNumber.trim().length === 0) {
                    errors.insurancePolicyNumber = 'Insurance policy number is required';
                }
            }

            // Legal Charter validation for buyer (step 4 is final step for buyer)
            if (role === 'buyer') {
                if (!charterAccepted) {
                    errors.charterAccepted = 'You must accept the legal charter to proceed';
                }
            }
        }

        if (step === 5) {
            // Legal Charter validation for specialized roles (step 5 is final step)
            if (!charterAccepted) {
                errors.charterAccepted = 'You must accept the legal charter to proceed';
            }
        }

        setStepErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleNextStep = () => {
        if (validateStep(currentStep)) {
            setStepErrors({});
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePreviousStep = () => {
        setStepErrors({});
        setCurrentStep(currentStep - 1);
    };

    // 📡 LOGIN SUBMIT HANDLER
    const handleLoginSubmitAction = async (e) => {
        e.preventDefault();
        setSubmittingPayload(true);
        setAuthError(null);
        const userEmailAddress = credentialsForm.email.toLowerCase().trim();
        const userAccessPasscode = credentialsForm.password;

        try {
            const res = await apiFetchWithRetry(`${baseUrl}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: userEmailAddress, password: userAccessPasscode })
            });
            const serverResponseData = await res.json();

            if (!res.ok) {
                setAuthError(serverResponseData.message || serverResponseData.error || "Credentials authorization refused.");
                setSubmittingPayload(false);
                return;
            }

            localStorage.setItem('ebakasiJwtToken', serverResponseData.token);
            localStorage.setItem('ebakasiUserData', JSON.stringify(serverResponseData.user));

            // Bypass OTP for admin credentials
            const isAdminLogin = serverResponseData.user?.role === "admin";


            if (isAdminLogin) {
                alert(`🎉 WELCOME BACK ADMIN: Direct access granted.`);
            } else {
                alert(`🎉 WELCOME BACK: Session token authenticated successfully.`);
            }

            if (onAuthSuccessBroadcast) onAuthSuccessBroadcast(serverResponseData.user);
            if (onCloseFormDrawer) onCloseFormDrawer();
        } catch (err) {
            setAuthError(err.message || "Service temporarily unavailable — please try again in a moment");
        } finally {
            setSubmittingPayload(false);
            setIsWakingUp(false);
        }
    };

    // 📡 BIOMETRIC AUTHENTICATION HANDLER
    const handleBiometricAuth = async (e) => {
        e.preventDefault();
        setSubmittingPayload(true);
        setAuthError(null);
        const userEmailAddress = credentialsForm.email.toLowerCase().trim();

        if (!userEmailAddress) {
            setAuthError('Please enter your email address first.');
            setSubmittingPayload(false);
            return;
        }

        try {
            // Get authentication options
            const optionsRes = await apiFetchWithRetry(`${baseUrl}/api/biometric/authenticate/options`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: userEmailAddress }),
            });

            const options = await optionsRes.json();
            if (!optionsRes.ok) {
                setAuthError(options.error || 'No biometric credentials found. Please register biometrics in Security Settings.');
                setSubmittingPayload(false);
                return;
            }

            // Start WebAuthn authentication
            const attResp = await startAuthentication(options);

            // Verify authentication
            const verifyRes = await apiFetchWithRetry(`${baseUrl}/api/biometric/authenticate/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: attResp, email: userEmailAddress }),
            });

            const verifyData = await verifyRes.json();
            if (verifyRes.ok) {
                localStorage.setItem('ebakasiJwtToken', verifyData.token);
                localStorage.setItem('ebakasiUserData', JSON.stringify(verifyData.user));
                alert(`🎉 WELCOME BACK: Biometric authentication successful.`);
                if (onAuthSuccessBroadcast) onAuthSuccessBroadcast(verifyData.user);
                if (onCloseFormDrawer) onCloseFormDrawer();
            } else {
                setAuthError(verifyData.error || 'Biometric authentication failed.');
            }
        } catch (err) {
            setAuthError(err.message || 'Biometric authentication failed. Make sure your device supports WebAuthn.');
        } finally {
            setSubmittingPayload(false);
            setIsWakingUp(false);
        }
    };

    // 📡 REGISTER SUBMIT HANDLER
    const handleRegisterSubmitAction = async (e) => {
        e.preventDefault();

        // Final validation before submission - use dynamic final step
        const finalStep = SPECIALIZED_ROLES.includes(credentialsForm.role) ? 5 : 4;
        if (!validateStep(finalStep)) {
            return;
        }

        setSubmittingPayload(true);
        setAuthError(null);
        const userEmailAddress = credentialsForm.email.toLowerCase().trim();

        // Combine dial code and national digits into full international number
        const fullPhoneNumber = `+${credentialsForm.phoneCountryDialCode}${credentialsForm.phoneNumber}`;

        // Add default commission values for backend compatibility
        const payloadWithDefaults = {
            ...credentialsForm,
            email: userEmailAddress,
            phone: fullPhoneNumber,
            confirmPassword: credentialsForm.confirmPassword, // Explicitly include for server-side validation
            suggestedCommission: credentialsForm.suggestedCommission || 10, // Default 10% for machinery_hire
            customFields: credentialsForm.customFields || {}, // Include custom registration fields
        };

        try {
            const res = await apiFetchWithRetry(`${baseUrl}/api/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payloadWithDefaults)
            });
            const serverResponseData = await res.json();

            if (!res.ok) {
                setAuthError(serverResponseData.message || serverResponseData.error || "Registration refused.");
                setSubmittingPayload(false);
                return;
            }

            // Store pending profile for OTP verification
            localStorage.setItem('ebakasiPendingUserProfile', JSON.stringify({
                email: userEmailAddress,
                name: credentialsForm.name,
                role: credentialsForm.role,
                phone: credentialsForm.phoneNumber
            }));

            // Set awaiting OTP state
            setIsAwaitingOtpVerification(true);
            setSubmittingPayload(false);

            // Immediately trigger email OTP dispatch
            await triggerChannelPinDispatch('email');
        } catch (err) {
            setAuthError(err.message || "Service temporarily unavailable — please try again in a moment");
        } finally {
            setSubmittingPayload(false);
            setIsWakingUp(false);
        }
    };

    // 📡 IDENTITY TRANSACTION CONTROLLER GATEWAY (LEGACY - KEPT FOR REFERENCE)
    const handleIdentitySubmitAction = async (e) => {
        e.preventDefault();
        setSubmittingPayload(true);
        const targetingNodePath = authActionTab === 'login' ? 'login' : 'register';
        const userEmailAddress = credentialsForm.email.toLowerCase().trim();
        const userAccessPasscode = credentialsForm.password;

        const formattedPayloadMatrix = authActionTab === 'login' 
            ? { email: userEmailAddress, password: userAccessPasscode }
            : { ...credentialsForm, email: userEmailAddress };

        try {
            const res = await fetch(`${baseUrl}/api/auth/${targetingNodePath}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formattedPayloadMatrix)
            });
            const serverResponseData = await res.json();

            if (!res.ok) {
                alert(serverResponseData.message || serverResponseData.error || "Credentials authorization refused.");
                setSubmittingPayload(false);
                return;
            }

            if (authActionTab === 'register') {
                alert("🎉 REGISTRATION SUCCESSFUL: Your profile has been created!");
                setSubmittingPayload(false);
                setIsAwaitingOtpVerification(false);
                if (onCloseFormDrawer) onCloseFormDrawer();
                return;
            }

            localStorage.setItem('ebakasiJwtToken', serverResponseData.token);
            localStorage.setItem('ebakasiUserData', JSON.stringify(serverResponseData.user));
            
            // Bypass OTP for admin credentials
            const isAdminLogin = serverResponseData.user?.role === "admin";

            
            if (isAdminLogin) {
                alert(`🎉 WELCOME BACK ADMIN: Direct access granted.`);
                if (onAuthSuccessBroadcast) onAuthSuccessBroadcast(serverResponseData.user);
                if (onCloseFormDrawer) onCloseFormDrawer();
            } else {
                alert(`🎉 WELCOME BACK: Session token authenticated successfully.`);
                if (onAuthSuccessBroadcast) onAuthSuccessBroadcast(serverResponseData.user);
                if (onCloseFormDrawer) onCloseFormDrawer();
            }
        } catch (err) {
            alert(err.message || "Service temporarily unavailable — please try again in a moment");
        } finally {
            setSubmittingPayload(false);
        }
    };

    // 📡 DUAL-CHANNEL OTP TRANSMITTER MATRIX
    const triggerChannelPinDispatch = async (channelType) => {
        setVerificationChannel(channelType);
        try {
            const res = await fetch(`${baseUrl}/api/auth-verify/request-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: credentialsForm.email, channel: channelType,
                    phoneNumber: credentialsForm.phoneNumber || credentialsForm.mobileMoneyLineNumber
                })
            });
            const data = await res.json();
            alert(data.message || data.error);
        } catch (err) {
            alert("Service temporarily unavailable — please try again in a moment");
        }
    };
    // 📡 6-DIGIT CORE VALIDATION VERIFIER
    const handleVerifyOtpSubmitAction = async (e) => {
        e.preventDefault();
        if (!clientSubmittedPin || clientSubmittedPin.trim().length !== 6) {
            alert("Security Trace: Enter your full 6-digit confirmation pin.");
            return;
        }
        try {
            const res = await fetch(`${baseUrl}/api/auth-verify/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: credentialsForm.email || JSON.parse(localStorage.getItem('ebakasiPendingUserProfile') || '{}').email,
                    clientSubmittedPin: clientSubmittedPin.trim()
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                // Clear pending profile
                localStorage.removeItem('ebakasiPendingUserProfile');
                setIsAwaitingOtpVerification(false);
                setClientSubmittedPin('');
                alert("✓ Cryptographic Channel Activation Complete. Your account is now in 24-hour credential vetting hold. You will receive full platform access automatically after the vetting period completes.");
                if (onCloseFormDrawer) onCloseFormDrawer();
            } else {
                alert(data.error || "PIN signature mismatch.");
            }
        } catch (err) {
            alert("Service temporarily unavailable — please try again in a moment");
        }
    };

    // 📡 FORGOT ACCESS KEY HANDLER ROUTINE
    const handleForgotPasswordAction = async (e) => {
        e.preventDefault();
        setSubmittingPayload(true);
        try {
            const res = await fetch(`${baseUrl}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: recoveryEmail })
            });
            const data = await res.json();
            if (res.ok) {
                alert("🎉 A 6-digit code has been sent to your email and phone. Enter it below to reset your password.");
                setIsAwaitingRecoveryPin(true);
            } else {
                alert(data.error || "Password recovery request denied.");
            }
        } catch (err) {
            alert("Service temporarily unavailable — please try again in a moment");
        } finally {
            setSubmittingPayload(false);
        }
    };

    // 📡 PASSWORD RESET EXECUTION ENGINE
    const handleResetPasswordAction = async (e) => {
        e.preventDefault();
        setSubmittingPayload(true);
        try {
            const res = await fetch(`${baseUrl}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: recoveryEmail, code: recoveryPin.trim(), newPassword: newPassword })
            });
            const data = await res.json();
            if (res.ok) {
                alert("🎉 PASSWORD MODIFIED SUCCESSFULLY! Please authenticate using your new password.");
                setAuthActionTab('login');
                setIsAwaitingRecoveryPin(false);
            } else {
                alert(data.error || "Reset validation failed.");
            }
        } catch (err) {
            alert("Service temporarily unavailable — please try again in a moment");
        } finally {
            setSubmittingPayload(false);
        }
    };
    if (isAwaitingOtpVerification) {
        const pendingProfile = JSON.parse(localStorage.getItem('ebakasiPendingUserProfile') || '{}');
        const last4Phone = pendingProfile.phone ? pendingProfile.phone.slice(-4) : 'XXXX';

    return (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px', boxSizing: 'border-box' }}>
                <div style={{ position: 'relative', padding: '25px', backgroundColor: 'var(--bg-card, #1e293b)', borderRadius: '12px', color: 'var(--text-primary, #fff)', border: '2px solid #f59e0b', maxWidth: '400px', width: '100%', boxSizing: 'border-box', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)', maxHeight: '90vh', overflowY: 'auto' }}>
                    <button type="button" onClick={() => setIsAwaitingOtpVerification(false)} style={{ position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', color: '#94a3b8', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', zIndex: 10 }}>✕</button>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <div style={{ fontSize: '36px', marginBottom: '10px' }}>🔑</div>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '900', letterSpacing: '-0.3px' }}>IDENTITY ACTIVATION CORE</h3>
                        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: 'var(--text-secondary, #94a3b8)', lineHeight: '1.5' }}>
                            A secure cryptographic access token has been dispatched to your institutional email and your mobile node ending in {last4Phone}.
                        </p>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                        <button type="button" onClick={() => triggerChannelPinDispatch('email')} style={{ flex: 1, padding: '10px 0', backgroundColor: 'var(--bg-main, #0f172a)', color: 'var(--text-primary, #fff)', border: '1px solid #475569', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>📨 Resend Email PIN</button>
                        <button type="button" onClick={() => triggerChannelPinDispatch('sms')} style={{ flex: 1, padding: '10px 0', backgroundColor: 'var(--bg-main, #0f172a)', color: 'var(--text-primary, #fff)', border: '1px solid #475569', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>💬 Send SMS PIN</button>
                    </div>

                    <form onSubmit={handleVerifyOtpSubmitAction} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', letterSpacing: '0.5px', display: 'block' }}>Enter 6-Digit Activation Key</label>
                            <input type="text" required maxLength="6" placeholder="e.g. 482910" value={clientSubmittedPin} onChange={e => setClientSubmittedPin(e.target.value.replace(/\D/g, ''))} style={{ width: '100%', padding: '12px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', textAlign: 'center', fontSize: '18px', fontWeight: '800', letterSpacing: '4px', outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <button type="submit" style={{ width: '100%', padding: '12px 0', backgroundColor: '#ea580c', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', textTransform: 'uppercase' }}>✓ Verify & Complete Channel Activation</button>
                    </form>
                </div>
            </div>
        );
    }

    const globalCountriesList = worldCountries.map(c => c.name);
    const ugandaDistrictsList = ugandaDistricts;
    const commercialBanksUganda = ["Stanbic Bank Uganda", "Centenary Bank", "DFCU Bank", "Absa Bank Uganda", "Standard Chartered", "Equity Bank Uganda", "KCB Bank Uganda", "Bank of Baroda", "International Swift Settlement Transfer"];
    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: isMobileViewport ? '12px' : '20px', boxSizing: 'border-box' }}>
            <div style={{ padding: isMobileViewport ? '16px' : '24px', backgroundColor: 'var(--bg-card, #1e293b)', borderRadius: '16px', color: 'var(--text-primary, #fff)', border: '1px solid var(--border-line, #334155)', maxWidth: '540px', width: '100%', boxSizing: 'border-box', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)', maxHeight: '92vh', overflowY: 'auto' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: '#f8fafc', letterSpacing: '-0.3px' }}>EBAKASI GATEWAY</h2>
                    <button type="button" onClick={onCloseFormDrawer} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary, #94a3b8)', fontSize: '20px', cursor: 'pointer', padding: '4px' }}>✕</button>
                </div>

                <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #0f172a', paddingBottom: '12px', marginBottom: '16px', overflowX: 'auto' }}>
                    <button type="button" onClick={() => setAuthActionTab('login')} style={{ flex: '1 0 auto', padding: '10px 12px', backgroundColor: authActionTab === 'login' ? '#ea580c' : '#0f172a', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '11px', cursor: 'pointer', textTransform: 'uppercase' }}>🔒 Sign-In</button>
                    <button type="button" onClick={() => setAuthActionTab('register')} style={{ flex: '1 0 auto', padding: '10px 12px', backgroundColor: authActionTab === 'register' ? '#ea580c' : '#0f172a', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '11px', cursor: 'pointer', textTransform: 'uppercase' }}>OPEN ACCOUNT</button>
                    <button type="button" onClick={() => setAuthActionTab('forgot')} style={{ flex: '1 0 auto', padding: '10px 12px', backgroundColor: authActionTab === 'forgot' ? '#ea580c' : '#0f172a', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '11px', cursor: 'pointer', textTransform: 'uppercase' }}>❓ Recovery</button>
                </div>

                {authActionTab === 'forgot' && (
                    <form onSubmit={isAwaitingRecoveryPin ? handleResetPasswordAction : handleForgotPasswordAction} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {!isAwaitingRecoveryPin ? (
                            <div>
                                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Registered Email Address</label>
                                <input type="email" required placeholder="e.g. mail@ebakasi.co.ug" value={recoveryEmail} onChange={e => setRecoveryEmail(e.target.value)} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                        <button type="submit" disabled={submittingPayload} style={{ width: '100%', padding: '12px 0', backgroundColor: '#ea580c', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', textTransform: 'uppercase', marginTop: '14px' }}>
                                    {submittingPayload ? "Processing..." : "🚀 Request Reset PIN"}
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>6-Digit Recovery Reset PIN</label>
                                    <input type="text" required maxLength="6" placeholder="e.g. 918273" value={recoveryPin} onChange={e => setRecoveryPin(e.target.value.replace(/\D/g, ''))} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box', textAlign: 'center', letterSpacing: '4px', fontWeight: '800' }} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Enter New Password</label>
                                    <input type="password" required placeholder="Min 6 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                                </div>

                        <button type="submit" disabled={submittingPayload} style={{ width: '100%', padding: '12px 0', backgroundColor: '#ea580c', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                    {submittingPayload ? "Saving Changes..." : "🔒 Lock New Password"}
                                </button>
                            </div>
                        )}
                    </form>
                )}
                {authActionTab !== 'forgot' && (
                    <form onSubmit={authActionTab === 'login' ? handleLoginSubmitAction : handleRegisterSubmitAction} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {/* Warm-up status indicator */}
                        {isWakingUp && (
                            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#0f172a', borderRadius: '6px', border: '1px solid #334155' }}>
                                <span style={{ fontSize: '12px', color: '#ea580c', fontWeight: '700' }}>Secure desk is initializing — hold on…</span>
                            </div>
                        )}

                        {/* Inline error display */}
                        {authError && (
                            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#450a0a', borderRadius: '6px', border: '1px solid #991b1b' }}>
                                <span style={{ fontSize: '12px', color: '#fca5a5', fontWeight: '700' }}>{authError}</span>
                            </div>
                        )}

                        {/* SIGNUP WIZARD - Only for registration */}
                        {authActionTab === 'register' && (
                            <>
                                {/* Step Indicator */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                                    {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
                                        <div key={step} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                backgroundColor: currentStep >= step ? '#ea580c' : '#334155',
                                                color: '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '12px',
                                                fontWeight: '800'
                                            }}>
                                                {currentStep > step ? '✓' : step}
                                            </div>
                                            {step < totalSteps && (
                                                <div style={{
                                                    width: '40px',
                                                    height: '2px',
                                                    backgroundColor: currentStep > step ? '#ea580c' : '#334155'
                                                }} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                                    <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '700' }}>
                                        Step {currentStep} of {totalSteps}
                                    </span>
                                </div>

                                {/* STEP 1: Profile Selection (Role Picker Only) */}
                                {currentStep === 1 && (
                                    <>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCredentialsForm({...credentialsForm, role: 'buyer'});
                                                    setRoleTransitioning(true);
                                                    setTimeout(() => {
                                                        setIsRoleSelected(true);
                                                        setCurrentStep(2);
                                                    }, 300);
                                                }}
                                                style={{
                                                    padding: '20px 12px',
                                                    backgroundColor: credentialsForm.role === 'buyer' && isRoleSelected ? '#ea580c' : '#0f172a',
                                                    color: 'var(--text-primary, #fff)',
                                                    border: '1px solid #475569',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: '800',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    transition: 'all 0.3s ease',
                                                    opacity: isRoleSelected && credentialsForm.role !== 'buyer' ? '0.3' : '1',
                                                    transform: isRoleSelected && credentialsForm.role !== 'buyer' ? 'scale(0.95)' : 'scale(1)'
                                                }}
                                            >
                                                <span style={{ fontSize: '28px' }}>🏛️</span>
                                                Corporate Procurement Node
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCredentialsForm({...credentialsForm, role: 'vendor'});
                                                    setRoleTransitioning(true);
                                                    setTimeout(() => {
                                                        setIsRoleSelected(true);
                                                        setCurrentStep(2);
                                                    }, 300);
                                                }}
                                                style={{
                                                    padding: '20px 12px',
                                                    backgroundColor: credentialsForm.role === 'vendor' && isRoleSelected ? '#ea580c' : '#0f172a',
                                                    color: 'var(--text-primary, #fff)',
                                                    border: '1px solid #475569',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: '800',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    transition: 'all 0.3s ease',
                                                    opacity: isRoleSelected && credentialsForm.role !== 'vendor' ? '0.3' : '1',
                                                    transform: isRoleSelected && credentialsForm.role !== 'vendor' ? 'scale(0.95)' : 'scale(1)'
                                                }}
                                            >
                                                <span style={{ fontSize: '28px' }}>🧱</span>
                                                Authorized Industrial Manufacturer
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCredentialsForm({...credentialsForm, role: 'machinery_hire'});
                                                    setRoleTransitioning(true);
                                                    setTimeout(() => {
                                                        setIsRoleSelected(true);
                                                        setCurrentStep(2);
                                                    }, 300);
                                                }}
                                                style={{
                                                    padding: '20px 12px',
                                                    backgroundColor: credentialsForm.role === 'machinery_hire' && isRoleSelected ? '#ea580c' : '#0f172a',
                                                    color: 'var(--text-primary, #fff)',
                                                    border: '1px solid #475569',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: '800',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    transition: 'all 0.3s ease',
                                                    opacity: isRoleSelected && credentialsForm.role !== 'machinery_hire' ? '0.3' : '1',
                                                    transform: isRoleSelected && credentialsForm.role !== 'machinery_hire' ? 'scale(0.95)' : 'scale(1)'
                                                }}
                                            >
                                                <span style={{ fontSize: '28px' }}>🏗️</span>
                                                Heavy Infrastructure Asset Lease Vault
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCredentialsForm({...credentialsForm, role: 'driver'});
                                                    setRoleTransitioning(true);
                                                    setTimeout(() => {
                                                        setIsRoleSelected(true);
                                                        setCurrentStep(2);
                                                    }, 300);
                                                }}
                                                style={{
                                                    padding: '20px 12px',
                                                    backgroundColor: credentialsForm.role === 'driver' && isRoleSelected ? '#ea580c' : '#0f172a',
                                                    color: 'var(--text-primary, #fff)',
                                                    border: '1px solid #475569',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: '800',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    transition: 'all 0.3s ease',
                                                    opacity: isRoleSelected && credentialsForm.role !== 'driver' ? '0.3' : '1',
                                                    transform: isRoleSelected && credentialsForm.role !== 'driver' ? 'scale(0.95)' : 'scale(1)'
                                                }}
                                            >
                                                <span style={{ fontSize: '28px' }}>🚛</span>
                                                Transit Cargo Driver
                                            </button>
                                        </div>
                                    </>
                                )}

                                {/* Role Badge - shown on steps 2-5 */}
                                {currentStep >= 2 && (
                                    <div
                                        onClick={() => {
                                            setIsRoleSelected(false);
                                            setCurrentStep(1);
                                        }}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '8px 16px',
                                            backgroundColor: '#ea580c',
                                            color: '#fff',
                                            borderRadius: '20px',
                                            fontSize: '12px',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            marginBottom: '16px',
                                            border: '1px solid #f59e0b'
                                        }}
                                    >
                                        {credentialsForm.role === 'buyer' && '🏛️ Corporate Procurement Node'}
                                        {credentialsForm.role === 'vendor' && '🧱 Authorized Industrial Manufacturer'}
                                        {credentialsForm.role === 'machinery_hire' && '🏗️ Heavy Infrastructure Asset Lease Vault'}
                                        {credentialsForm.role === 'driver' && '🚛 Transit Cargo Driver'}
                                        <span style={{ fontSize: '10px', opacity: 0.8, marginLeft: '8px' }}>✕ Change</span>
                                    </div>
                                )}

                                {/* STEP 2: Access Credentials */}
                                {currentStep === 2 && (
                                    <>
                                        <div>
                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Full Name / Representative Identity</label>
                                            <input type="text" required placeholder="e.g. Lawrence Omoit" value={credentialsForm.name} onChange={e => setCredentialsForm({...credentialsForm, name: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                                            {stepErrors.name && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.name}</span>}
                                        </div>

                                        <div>
                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Email Address</label>
                                            <input type="email" required placeholder="e.g. corporate@ebakasi.co.ug" value={credentialsForm.email} onChange={e => setCredentialsForm({...credentialsForm, email: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                                            {stepErrors.email && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.email}</span>}
                                        </div>

                                        <div style={{ background: '#0f172a', padding: isMobileViewport ? '10px' : '12px', borderRadius: '8px', border: '1px solid var(--border-line, #334155)', boxSizing: 'border-box' }}>
                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Primary Secure Mobile Node</label>
                                                <span style={{ fontSize: '10px', color: '#94a3b8', display: 'block', marginBottom: '6px' }}>This line will be used for secure transactional verification.</span>
                                                <div style={{ display: 'flex', gap: isMobileViewport ? '6px' : '8px', width: '100%', boxSizing: 'border-box' }}>
                                                    <div style={{ flex: '0 0 auto', minWidth: isMobileViewport ? '92px' : '140px', width: isMobileViewport ? '92px' : 'auto' }}>
                                                        <input
                                                            type="text"
                                                            list="country-code-datalist"
                                                            required
                                                            placeholder="+256"
                                                            value={`+${credentialsForm.phoneCountryDialCode}`}
                                                            onChange={e => {
                                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                                setCredentialsForm({...credentialsForm, phoneCountryDialCode: value});
                                                            }}
                                                            style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: isMobileViewport ? '16px' : '13px', boxSizing: 'border-box', minWidth: 0 }}
                                                        />
                                                        <datalist id="country-code-datalist">
                                                            {(worldCountries || []).map((c, i) => (
                                                                <option key={i} value={`+${c.dialCode}`}>
                                                                    {c.name} (+{c.dialCode})
                                                                </option>
                                                            ))}
                                                        </datalist>
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0, boxSizing: 'border-box' }}>
                                                        <input
                                                            type="tel"
                                                            inputMode="numeric"
                                                            required
                                                            placeholder="772000000"
                                                            value={credentialsForm.phoneNumber}
                                                            onChange={e => {
                                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                                setCredentialsForm({...credentialsForm, phoneNumber: value});
                                                            }}
                                                            style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: isMobileViewport ? '16px' : '13px', boxSizing: 'border-box', minWidth: 0 }}
                                                        />
                                                    </div>
                                                </div>
                                                {stepErrors.phoneNumber && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.phoneNumber}</span>}
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Password</label>
                                                <input type="password" required placeholder="Min 8 characters" value={credentialsForm.password} onChange={e => setCredentialsForm({...credentialsForm, password: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                                                {stepErrors.password && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.password}</span>}
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Confirm Password</label>
                                                <input type="password" required placeholder="Re-enter password" value={credentialsForm.confirmPassword} onChange={e => setCredentialsForm({...credentialsForm, confirmPassword: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                                                {stepErrors.confirmPassword && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.confirmPassword}</span>}
                                            </div>
                                        </div>

                                        {/* Custom Registration Fields (SUPER_ADMIN-configurable) */}
                                        {registrationFormSchema.length > 0 && (
                                            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#0f172a', borderRadius: '8px', border: '1px solid var(--border-line, #334155)' }}>
                                                <label style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: '#f59e0b', display: 'block', marginBottom: '8px' }}>Additional Information</label>
                                                {registrationFormSchema
                                                    .filter(field => !field.targetRole || field.targetRole === credentialsForm.role)
                                                    .map((field) => (
                                                    <div key={field.fieldId} style={{ marginBottom: '12px' }}>
                                                        <label style={{ fontSize: '11px', fontWeight: '700', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>
                                                            {field.fieldName}
                                                            {field.required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
                                                        </label>
                                                        {field.fieldType === 'checkbox' ? (
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-primary, #fff)' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={credentialsForm.customFields?.[field.fieldId] || false}
                                                                    onChange={(e) => setCredentialsForm({
                                                                        ...credentialsForm,
                                                                        customFields: {
                                                                            ...(credentialsForm.customFields || {}),
                                                                            [field.fieldId]: e.target.checked
                                                                        }
                                                                    })}
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                                {field.placeholder}
                                                            </label>
                                                        ) : field.fieldType === 'select' ? (
                                                            <select
                                                                required={field.required}
                                                                value={credentialsForm.customFields?.[field.fieldId] || ''}
                                                                onChange={(e) => setCredentialsForm({
                                                                    ...credentialsForm,
                                                                    customFields: {
                                                                        ...(credentialsForm.customFields || {}),
                                                                        [field.fieldId]: e.target.value
                                                                    }
                                                                })}
                                                                style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                                                            >
                                                                <option value="">Select...</option>
                                                                {field.options?.map((opt, i) => (
                                                                    <option key={i} value={opt}>{opt}</option>
                                                                ))}
                                                            </select>
                                                        ) : field.fieldType === 'textarea' ? (
                                                            <textarea
                                                                required={field.required}
                                                                placeholder={field.placeholder || ''}
                                                                value={credentialsForm.customFields?.[field.fieldId] || ''}
                                                                onChange={(e) => setCredentialsForm({
                                                                    ...credentialsForm,
                                                                    customFields: {
                                                                        ...(credentialsForm.customFields || {}),
                                                                        [field.fieldId]: e.target.value
                                                                    }
                                                                })}
                                                                rows={3}
                                                                style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', resize: 'vertical' }}
                                                            />
                                                        ) : (
                                                            <input
                                                                type={field.fieldType}
                                                                required={field.required}
                                                                placeholder={field.placeholder || ''}
                                                                value={credentialsForm.customFields?.[field.fieldId] || ''}
                                                                onChange={(e) => setCredentialsForm({
                                                                    ...credentialsForm,
                                                                    customFields: {
                                                                        ...(credentialsForm.customFields || {}),
                                                                        [field.fieldId]: e.target.value
                                                                    }
                                                                })}
                                                                style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                                                            />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                            <button type="button" onClick={handleNextStep} style={{ flex: 1, padding: '12px 0', backgroundColor: '#ea580c', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                                Next Step →
                                            </button>
                                        </div>
                                    </>
                                )}

                                {/* STEP 3: Location (All Roles) + NIN (Buyer) */}
                                {currentStep === 3 && (
                                    <>
                                        {/* 📍 CONSOLIDATED LOCATION BLOCK */}
                                        <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-line, #334155)', display: 'flex', flexDirection: 'column', gap: '12px', boxSizing: 'border-box' }}>
                                            <label style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: '#f59e0b', display: 'block', marginBottom: '4px', textAlign: 'center' }}>📍 OPERATIONAL LOCATION</label>

                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Country</label>
                                                <input type="text" list="country-datalist" placeholder="Search or scroll countries" value={credentialsForm.location.country} onChange={e => setCredentialsForm({...credentialsForm, location: {...credentialsForm.location, country: e.target.value, district: '', subcounty: ''}})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                <datalist id="country-datalist">
                                                    {(worldCountries || []).map((c, i) => <option key={i} value={c.name}>{c.name}</option>)}
                                                </datalist>
                                            </div>

                                            {credentialsForm.location.country === 'Uganda' ? (
                                                <>
                                                    <div style={{ position: "relative" }}>
                                                        <label style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#cbd5e1", display: "block", marginBottom: "4px" }}>District (Type to Search) *</label>
                                                        <input
                                                            type="text"
                                                            placeholder="e.g. Buliisa, Kampala, Soroti, Jinja"
                                                            value={credentialsForm.location.district ? credentialsForm.location.district : districtSearchQuery}
                                                            onFocus={() => {
                                                              setIsDistrictDropdownOpen(true);
                                                              if (credentialsForm.location.district) {
                                                                setDistrictSearchQuery(credentialsForm.location.district);
                                                                setCredentialsForm({...credentialsForm, location: {...credentialsForm.location, district: "", subcounty: ""}});
                                                              }
                                                            }}
                                                            onChange={e => {
                                                              setDistrictSearchQuery(e.target.value);
                                                              setIsDistrictDropdownOpen(true);
                                                            }}
                                                            style={{ width: "100%", padding: "10px", backgroundColor: "var(--bg-card, #1e293b)", border: "1px solid #475569", color: "var(--text-primary, #fff)", borderRadius: "6px", fontSize: "16px", boxSizing: "border-box", outline: "none" }}
                                                        />
                                                        {stepErrors.district && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.district}</span>}
                                                        {isDistrictDropdownOpen && (
                                                            <div style={{ position: "absolute", top: "100%", left: 0, width: "100%", backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "6px", maxHeight: "180px", overflowY: "auto", zIndex: 10000, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.5)", marginTop: "4px" }}>
                                                                {(() => {
                                                                    const matched = (ugandaDistricts || []).filter(d => d.toLowerCase().includes(districtSearchQuery.toLowerCase()));
                                                                    if (matched.length === 0) {
                                                                        return <div style={{ padding: "10px", color: "#94a3b8", fontSize: "13px" }}>No matching districts found</div>;
                                                                    }
                                                                    return matched.map((d, i) => (
                                                                        <div
                                                                            key={i}
                                                                            onClick={() => {
                                                                                setCredentialsForm({...credentialsForm, location: {...credentialsForm.location, district: d, subcounty: ""}});
                                                                                setDistrictSearchQuery(d);
                                                                                setIsDistrictDropdownOpen(false);
                                                                            }}
                                                                            style={{ padding: "10px", color: "#fff", cursor: "pointer", fontSize: "14px", borderBottom: "1px solid #334155" }}
                                                                            onMouseEnter={e => e.target.style.backgroundColor = "#ea580c"}
                                                                            onMouseLeave={e => e.target.style.backgroundColor = "transparent"}
                                                                        >
                                                                            🇺🇬 {d}
                                                                        </div>
                                                                    ));
                                                                })()}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {credentialsForm.location.district && (
                                                        <div>
                                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Subcounty</label>
                                                            <select
                                                                value={credentialsForm.location.subcounty}
                                                                onChange={e => setCredentialsForm({...credentialsForm, location: {...credentialsForm.location, subcounty: e.target.value}})}
                                                                style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '16px', boxSizing: 'border-box', outline: 'none' }}
                                                            >
                                                                <option value="">Select Subcounty</option>
                                                                {(ugandaSubcounties[credentialsForm.location.district] || []).map((s, i) => (
                                                                    <option key={i} value={s}>{s}</option>
                                                                ))}
                                                            </select>
                                                            {stepErrors.subcounty && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.subcounty}</span>}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div>
                                                    <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Region / State</label>
                                                    <input type="text" placeholder="e.g. California, Ontario" value={credentialsForm.location.district} onChange={e => setCredentialsForm({...credentialsForm, location: {...credentialsForm.location, district: e.target.value}})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                </div>
                                            )}

                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Village / Street *</label>
                                                <div style={{ position: 'relative' }}>
                                                    <input
                                                        type="text"
                                                        required
                                                        placeholder={credentialsForm.location.subcounty ? "e.g. Nakasero" : "Select subcounty first"}
                                                        value={credentialsForm.location.villageStreet}
                                                        onChange={handleVillageInputChange}
                                                        disabled={!credentialsForm.location.subcounty}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px',
                                                            backgroundColor: credentialsForm.location.subcounty ? 'var(--bg-card, #1e293b)' : '#2d3748',
                                                            border: '1px solid #475569',
                                                            color: credentialsForm.location.subcounty ? 'var(--text-primary, #fff)' : '#718096',
                                                            borderRadius: '6px',
                                                            fontSize: '13px',
                                                            boxSizing: 'border-box',
                                                            cursor: credentialsForm.location.subcounty ? 'text' : 'not-allowed'
                                                        }}
                                                    />
                                                    {stepErrors.villageStreet && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.villageStreet}</span>}
                                                    {showVillageSuggestions && villageSuggestions.length > 0 && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: 0,
                                                            right: 0,
                                                            backgroundColor: 'var(--bg-card, #1e293b)',
                                                            border: '1px solid #475569',
                                                            borderRadius: '6px',
                                                            marginTop: '4px',
                                                            maxHeight: '200px',
                                                            overflowY: 'auto',
                                                            zIndex: 1000,
                                                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                                                        }}>
                                                            {villageSuggestions.map((village, index) => (
                                                                <div
                                                                    key={index}
                                                                    onClick={() => handleVillageSelect(village)}
                                                                    style={{
                                                                        padding: '10px 12px',
                                                                        cursor: 'pointer',
                                                                        borderBottom: index < villageSuggestions.length - 1 ? '1px solid #475569' : 'none',
                                                                        transition: 'background-color 0.2s'
                                                                    }}
                                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2d3748'}
                                                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                                >
                                                                    {village}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {isCustomVillage && credentialsForm.location.villageStreet.length >= 2 && (
                                                        <div style={{
                                                            marginTop: '8px',
                                                            padding: '8px 12px',
                                                            backgroundColor: '#2d3748',
                                                            border: '1px solid #f59e0b',
                                                            borderRadius: '6px',
                                                            fontSize: '11px',
                                                            color: '#f59e0b'
                                                        }}>
                                                            ⚠️ Village not found in database. You can type it manually.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Specific Address (Plot / Yard Gate) *</label>
                                                <input type="text" required placeholder="e.g. Plot 15, Industrial Area Street" value={credentialsForm.location.specificAddress} onChange={e => setCredentialsForm({...credentialsForm, location: {...credentialsForm.location, specificAddress: e.target.value}})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                {stepErrors.specificAddress && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.specificAddress}</span>}
                                            </div>

                                            <button type="button" onClick={handleGpsCapture} style={{ width: '100%', padding: '12px 0', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '12px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                                📍 Capture GPS Coordinates
                                            </button>
                                            {gpsMessage && (
                                                <div style={{ fontSize: '11px', color: '#f59e0b', textAlign: 'center', marginTop: '4px' }}>{gpsMessage}</div>
                                            )}
                                        </div>

                                        {/* Buyer: NIN Field */}
                                        {credentialsForm.role === 'buyer' && (
                                            <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-line, #334155)', display: 'flex', flexDirection: 'column', gap: '12px', boxSizing: 'border-box' }}>
                                                <label style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: '#f59e0b', display: 'block', marginBottom: '4px', textAlign: 'center' }}>🆔 IDENTITY VERIFICATION</label>
                                                <div>
                                                    <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>National ID / NIN Number</label>
                                                    <input type="text" placeholder="e.g. CF12345678901A" value={credentialsForm.nationalIdNinNumber || ''} onChange={e => setCredentialsForm({...credentialsForm, nationalIdNinNumber: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                                                    {stepErrors.nationalIdNinNumber && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.nationalIdNinNumber}</span>}
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                            <button type="button" onClick={handlePreviousStep} style={{ flex: 1, padding: '12px 0', backgroundColor: '#334155', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                                ← Back
                                            </button>
                                            <button type="button" onClick={handleNextStep} style={{ flex: 1, padding: '12px 0', backgroundColor: '#ea580c', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                                {credentialsForm.role === 'buyer' ? 'Review & Submit →' : 'Next Step →'}
                                            </button>
                                        </div>
                                    </>
                                )}

                                {/* STEP 4: Role-specific Credentials (Specialized Roles) or Legal (Buyer) */}
                                {currentStep === 4 && (
                                    <>
                                        {/* Buyer: Legal Charter + Submit */}
                                        {credentialsForm.role === 'buyer' && (
                                            <>
                                                <div style={{ padding: '16px', backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid #f59e0b', borderRadius: '12px', boxSizing: 'border-box' }}>
                                                    <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '800', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        📜 Uganda Electronic Transactions Act 2011
                                                    </h4>
                                                    <p style={{ margin: '0 0 12px 0', fontSize: '11px', color: '#94a3b8', lineHeight: '1.5' }}>
                                                        {legalCharter.registrationAcknowledgment}
                                                    </p>
                                                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '11px', color: '#e2e8f0', cursor: 'pointer', fontWeight: '700' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={charterAccepted}
                                                            onChange={(e) => setCharterAccepted(e.target.checked)}
                                                            style={{ marginRight: '8px', accentColor: '#f59e0b', width: '16px', height: '16px' }}
                                                        />
                                                        I acknowledge and accept the Electronic Transactions Act legal charter
                                                    </label>
                                                    {stepErrors.charterAccepted && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '4px' }}>{stepErrors.charterAccepted}</span>}
                                                    <span
                                                        onClick={() => setShowLegalCharter(true)}
                                                        style={{ display: 'block', marginTop: '10px', fontSize: '11px', color: '#ea580c', cursor: 'pointer', textDecoration: 'underline', fontWeight: '700' }}
                                                    >
                                                        Read full Legal Charter →
                                                    </span>
                                                </div>

                                                <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                                                    <button type="button" onClick={handlePreviousStep} style={{ flex: 1, padding: '12px 0', backgroundColor: '#334155', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                                        ← Back
                                                    </button>
                                                    <button type="submit" disabled={!charterAccepted || submittingPayload} style={{ flex: 1, padding: '12px 0', backgroundColor: (!charterAccepted || submittingPayload) ? '#334155' : '#ea580c', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: (!charterAccepted || submittingPayload) ? 'not-allowed' : 'pointer', textTransform: 'uppercase' }}>
                                                        {submittingPayload ? "Processing..." : "🚀 Create Platform Account"}
                                                    </button>
                                                </div>
                                            </>
                                        )}

                                        {/* Specialized Roles: Role-specific Credentials */}
                                        {SPECIALIZED_ROLES.includes(credentialsForm.role) && (
                                            <>
                                                {/* Vendor-specific fields */}
                                                {credentialsForm.role === 'vendor' && (
                                                    <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-line, #334155)', display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box' }}>
                                                        <div>
                                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Merchant Name / Supply Yard Depot</label>
                                                            <input type="text" required placeholder="e.g. Tororo Cement Depot" value={credentialsForm.enterprisePlantName} onChange={e => setCredentialsForm({...credentialsForm, enterprisePlantName: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                            {stepErrors.enterprisePlantName && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.enterprisePlantName}</span>}
                                                        </div>
                                                        <div>
                                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>National ID / NIN Number</label>
                                                            <input type="text" required placeholder="e.g. CF12345678901A" value={credentialsForm.nationalIdNinNumber || ''} onChange={e => setCredentialsForm({...credentialsForm, nationalIdNinNumber: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                                                            {stepErrors.nationalIdNinNumber && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.nationalIdNinNumber}</span>}
                                                        </div>
                                                        <div style={{ borderTop: '1px solid #334155', paddingTop: '12px' }}>
                                                            <label style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: '#f59e0b', display: 'block', marginBottom: '6px' }}>Settlement Channel</label>
                                                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                                                <button type="button" onClick={() => setCredentialsForm({...credentialsForm, payoutMethod: 'bank'})} style={{ flex: 1, padding: '10px 0', backgroundColor: '#ea580c', color: 'var(--text-primary, #fff)', border: '1px solid #f59e0b', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>🏦 Bank Wire</button>
                                                            </div>
                                                            <div style={{ fontSize: '10px', color: '#94a3b8', fontStyle: 'italic', marginTop: '4px' }}>
                                                                Bank Wire only for vendor settlements
                                                            </div>
                                                        </div>
                                                        {credentialsForm.payoutMethod === 'bank' && (
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                                <div>
                                                                    <label style={{ fontSize: '11px', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Select Commercial Bank</label>
                                                                    <select value={credentialsForm.bankTargetName} onChange={e => setCredentialsForm({...credentialsForm, bankTargetName: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '12px' }}>
                                                                        {commercialBanksUganda.map((b, i) => <option key={i} value={b}>{b}</option>)}
                                                                    </select>
                                                                </div>
                                                                <div>
                                                                    <label style={{ fontSize: '11px', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Bank Account Number</label>
                                                                    <input type="text" required placeholder="e.g. 903000..." value={credentialsForm.bankAccountNumberId} onChange={e => setCredentialsForm({...credentialsForm, bankAccountNumberId: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                                    {stepErrors.bankAccountNumberId && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.bankAccountNumberId}</span>}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {credentialsForm.payoutMethod === 'momo' && (
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                                <div>
                                                                    <label style={{ fontSize: '11px', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Merchant Code</label>
                                                                    <input type="text" required placeholder="e.g. 151234" value={credentialsForm.merchantSettlementPayoutCode} onChange={e => setCredentialsForm({...credentialsForm, merchantSettlementPayoutCode: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                                    {stepErrors.merchantSettlementPayoutCode && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.merchantSettlementPayoutCode}</span>}
                                                                </div>
                                                                <div>
                                                                    <label style={{ fontSize: '11px', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Payout Phone Line</label>
                                                                    <input type="text" required placeholder="e.g. 0772..." value={credentialsForm.mobileMoneyLineNumber} onChange={e => setCredentialsForm({...credentialsForm, mobileMoneyLineNumber: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                                    {stepErrors.mobileMoneyLineNumber && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.mobileMoneyLineNumber}</span>}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Machinery Hire-specific fields */}
                                                {credentialsForm.role === 'machinery_hire' && (
                                                    <div style={{ background: '#0f172a', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-line, #334155)', display: 'flex', flexDirection: 'column', gap: '12px', boxSizing: 'border-box' }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                            <div>
                                                                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Primary Machinery Type</label>
                                                                <select value={credentialsForm.primaryMachineryType} onChange={e => setCredentialsForm({...credentialsForm, primaryMachineryType: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px' }}>
                                                                    <option value="Excavators">Excavators</option>
                                                                    <option value="Bulldozers">Bulldozers</option>
                                                                    <option value="Loaders">Loaders</option>
                                                                    <option value="Cranes">Cranes</option>
                                                                    <option value="Tractors">Tractors</option>
                                                                    <option value="Harvesters">Harvesters</option>
                                                                    <option value="Generators">Generators</option>
                                                                    <option value="Compressors">Compressors</option>
                                                                    <option value="Concrete Mixers">Concrete Mixers</option>
                                                                    <option value="Rollers">Rollers</option>
                                                                    <option value="Graders">Graders</option>
                                                                    <option value="Backhoes">Backhoes</option>
                                                                    <option value="Forklifts">Forklifts</option>
                                                                    <option value="Other">Other Heavy Machinery</option>
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Fleet Size</label>
                                                                <select value={credentialsForm.machineryFleetSize} onChange={e => setCredentialsForm({...credentialsForm, machineryFleetSize: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px' }}>
                                                                    <option value="1-5 Units">1-5 Units</option>
                                                                    <option value="6-15 Units">6-15 Units</option>
                                                                    <option value="Over 15 Units">Over 15 Heavy Units</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Permit Registration No.</label>
                                                            <input type="text" required placeholder="e.g. MOW/HE/..." value={credentialsForm.permitRegistrationNumber} onChange={e => setCredentialsForm({...credentialsForm, permitRegistrationNumber: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                            {stepErrors.permitRegistrationNumber && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.permitRegistrationNumber}</span>}
                                                        </div>
                                                        <div>
                                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Suggested Commission %</label>
                                                            <input type="text" placeholder="e.g. 10" value={credentialsForm.suggestedCommission} onChange={e => setCredentialsForm({...credentialsForm, suggestedCommission: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                        </div>
                                                        <div>
                                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>National ID / NIN Number</label>
                                                            <input type="text" required placeholder="e.g. CF12345678901A" value={credentialsForm.nationalIdNinNumber || ''} onChange={e => setCredentialsForm({...credentialsForm, nationalIdNinNumber: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                                                            {stepErrors.nationalIdNinNumber && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.nationalIdNinNumber}</span>}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Driver-specific fields */}
                                                {credentialsForm.role === 'driver' && (
                                                    <div style={{ background: '#0f172a', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-line, #334155)', display: 'flex', flexDirection: 'column', gap: '12px', boxSizing: 'border-box' }}>
                                                        <div>
                                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Driving License Number</label>
                                                            <input type="text" required placeholder="e.g. DL-UG-..." value={credentialsForm.drivingLicenseNumber} onChange={e => setCredentialsForm({...credentialsForm, drivingLicenseNumber: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                            {stepErrors.drivingLicenseNumber && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.drivingLicenseNumber}</span>}
                                                        </div>
                                                        <div>
                                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Truck Registration Plates</label>
                                                            <input type="text" required placeholder="e.g. UBB 123X, UBD 456Y" value={credentialsForm.truckRegistrationPlates} onChange={e => setCredentialsForm({...credentialsForm, truckRegistrationPlates: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                            {stepErrors.truckRegistrationPlates && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.truckRegistrationPlates}</span>}
                                                        </div>
                                                        <div>
                                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Insurance Policy Number</label>
                                                            <input type="text" required placeholder="e.g. IRA/UL/2026/..." value={credentialsForm.insurancePolicyNumber} onChange={e => setCredentialsForm({...credentialsForm, insurancePolicyNumber: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-card, #1e293b)', border: '1px solid #475569', color: 'var(--text-primary, #fff)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                                                            {stepErrors.insurancePolicyNumber && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.insurancePolicyNumber}</span>}
                                                        </div>
                                                        <div>
                                                            <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>National ID / NIN Number</label>
                                                            <input type="text" required placeholder="e.g. CF12345678901A" value={credentialsForm.nationalIdNinNumber || ''} onChange={e => setCredentialsForm({...credentialsForm, nationalIdNinNumber: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                                                            {stepErrors.nationalIdNinNumber && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '2px' }}>{stepErrors.nationalIdNinNumber}</span>}
                                                        </div>
                                                    </div>
                                                )}

                                                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                                    <button type="button" onClick={handlePreviousStep} style={{ flex: 1, padding: '12px 0', backgroundColor: '#334155', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                                        ← Back
                                                    </button>
                                                    <button type="button" onClick={handleNextStep} style={{ flex: 1, padding: '12px 0', backgroundColor: '#ea580c', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                                        Next Step →
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                {/* STEP 5: Legal Charter + Submit (Specialized Roles Only) */}
                                {currentStep === 5 && (
                                    <>
                                        <div style={{ padding: '16px', backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid #f59e0b', borderRadius: '12px', boxSizing: 'border-box' }}>
                                            <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '800', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                📜 Uganda Electronic Transactions Act 2011
                                            </h4>
                                            <p style={{ margin: '0 0 12px 0', fontSize: '11px', color: '#94a3b8', lineHeight: '1.5' }}>
                                                {legalCharter.registrationAcknowledgment}
                                            </p>
                                            <label style={{ display: 'flex', alignItems: 'center', fontSize: '11px', color: '#e2e8f0', cursor: 'pointer', fontWeight: '700' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={charterAccepted}
                                                    onChange={(e) => setCharterAccepted(e.target.checked)}
                                                    style={{ marginRight: '8px', accentColor: '#f59e0b', width: '16px', height: '16px' }}
                                                />
                                                I acknowledge and accept the Electronic Transactions Act legal charter
                                            </label>
                                            {stepErrors.charterAccepted && <span style={{ fontSize: '10px', color: '#ef4444', display: 'block', marginTop: '4px' }}>{stepErrors.charterAccepted}</span>}
                                            <span
                                                onClick={() => setShowLegalCharter(true)}
                                                style={{ display: 'block', marginTop: '10px', fontSize: '11px', color: '#ea580c', cursor: 'pointer', textDecoration: 'underline', fontWeight: '700' }}
                                            >
                                                Read full Legal Charter →
                                            </span>
                                        </div>

                                        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                                            <button type="button" onClick={handlePreviousStep} style={{ flex: 1, padding: '12px 0', backgroundColor: '#334155', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', textTransform: 'uppercase' }}>
                                                ← Back
                                            </button>
                                            <button type="submit" disabled={!charterAccepted || submittingPayload} style={{ flex: 1, padding: '12px 0', backgroundColor: (!charterAccepted || submittingPayload) ? '#334155' : '#ea580c', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: (!charterAccepted || submittingPayload) ? 'not-allowed' : 'pointer', textTransform: 'uppercase' }}>
                                                {submittingPayload ? "Processing..." : "🚀 Create Platform Account"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {/* LOGIN FORM - Unchanged */}
                        {authActionTab === 'login' && (
                            <>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <div>
                                        <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Email Address</label>
                                        <input type="email" required placeholder="e.g. corporate@ebakasi.co.ug" value={credentialsForm.email} onChange={e => setCredentialsForm({...credentialsForm, email: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#cbd5e1', display: 'block', marginBottom: '4px' }}>Password</label>
                                        <input type="password" required placeholder="Min 8 characters" value={credentialsForm.password} onChange={e => setCredentialsForm({...credentialsForm, password: e.target.value})} style={{ width: '100%', padding: '10px', backgroundColor: 'var(--bg-main, #0f172a)', border: '1px solid var(--border-line, #334155)', color: 'var(--text-primary, #fff)', borderRadius: '6px', outline: 'none', fontSize: '13px', boxSizing: 'border-box' }} />
                                    </div>
                                </div>

                                <button type="submit" disabled={submittingPayload} style={{ width: '100%', padding: '13px 0', backgroundColor: '#ea580c', color: 'var(--text-primary, #fff)', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '13px', cursor: submittingPayload ? 'not-allowed' : 'pointer', textTransform: 'uppercase', marginTop: '10px' }}>
                                    {submittingPayload ? "Processing Credentials..." : "🔑 Request Identity Token"}
                                </button>

                                {/* Biometric Authentication Button */}
                                <button
                                    type="button"
                                    onClick={handleBiometricAuth}
                                    disabled={submittingPayload}
                                    style={{
                                        width: '100%',
                                        padding: '13px 0',
                                        backgroundColor: '#334155',
                                        color: 'var(--text-primary, #fff)',
                                        border: '1px solid #475569',
                                        borderRadius: '6px',
                                        fontWeight: '700',
                                        fontSize: '13px',
                                        cursor: submittingPayload ? 'not-allowed' : 'pointer',
                                        marginTop: '12px'
                                    }}
                                >
                                    {submittingPayload ? "Authenticating..." : "📱 Sign in with Biometrics"}
                                </button>

                                {/* Google OAuth Button - only renders if client ID is configured */}
                                {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
                                    <div style={{ marginTop: '12px' }}>
                                        <div id="google-signin-button" style={{ width: '100%' }}></div>
                                    </div>
                                )}
                            </>
                        )}
                    </form>
                )}
            </div>

            <LegalCharterModal isOpen={showLegalCharter} onClose={() => setShowLegalCharter(false)} />
        </div>
    );
}
